// src/components/TemplateEditor/mergeFieldDefinitions.js
//
// Source-of-truth definitions for all merge fields available in the Document
// Templates editor. Categories mirror the layout we settled on (matching
// boatclubapp's structure for familiar UX for migrating providers).
//
// Phase A.2b: system fields only (Customer, Booking, Customer Input,
// Signatures & Initials, Dates). Provider info is intentionally NOT
// tokenized — a provider types their own business name / address / contact
// info as plain text once per template (it doesn't change per booking, so
// merge-field overhead isn't worth it). Same rationale that dropped
// `businessName` from the schema earlier in the design.
// Phase A.3: Custom Parameters category becomes dynamic (per-provider, from DB).
//
// Each field has:
//   key       — the canonical token used in the template body
//               (e.g. 'customer.firstName')
//   label     — human-readable label shown in palette and in chip
//   category  — palette grouping (must be one of MERGE_FIELD_CATEGORIES)
//
// The chip renders as a styled pill with the label. The serialized form
// (in Tiptap JSON) stores the key in node.attrs.fieldKey. At rendering time
// (Phase B/C), tokens resolve to actual values from booking/customer/provider
// data via the same merge-field substitution code that the PDF generator runs.

// 'customFields' is the renamed-from-'customParameters' category as of
// Phase A.3.3 (rename at the rendering boundary only — the DB column,
// Redux slice field, and API payload field stay named 'custom_parameters'
// per locked decision E. The projection client-side: editTemplate.
// custom_parameters → palette prop customFields.)
//
// Phase A.4a: 'customerInput' merged into 'customer'. The Customer/Customer
// Input split was a today-snapshot of which fields exist in the BN profile
// vs. always-customer-input. As BN profile expands (DOB, DL, etc. likely
// added in v1.1), the split becomes increasingly arbitrary. Unifying gives
// a stable mental model: "customer-related chips" auto-merge from profile
// when data exists, fall back to customer-input when not.
//
// All 17 customer-related fields share the same runtime behavior at
// signing time: smart-merge with required customer-input fallback. The
// provider doesn't reason about which is which at authoring time. Field
// keys preserve the existing prefixes (`customer.*` and `input.*`) — the
// signing-time resolver and existing chip content_html don't change.
//
// A.4a fix-pass v2 follow-up: category render order reflects the typical
// document narrative — open with the Execution anchors at the top of the
// palette (signature + initials + the two computed date chips, all about
// the document's execution), then booking-level (Reservation) details,
// then per-customer fields, then provider-defined custom fields at the
// end. The 'signatures' key is unchanged in the data model; only the
// user-facing CATEGORY_LABELS render uses "Execution".
//
// A.4a fix-pass v2 follow-up addendum: the standalone Dates category was
// collapsed into Execution — Today's Date and Signed At are conceptually
// document-execution metadata and live alongside the signature/initial
// chips. The 'dates' category key is retired; both date chips now have
// `category: 'signatures'`.
export const MERGE_FIELD_CATEGORIES = [
  'signatures',
  'booking',
  'customer',
  'customFields',
];

// Phase A.4a: enforces the multi-signer model in CreateTemplateModal's
// dropdown filter. Mirrors VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS in
// server/api/document-template-utils.js (server is canonical; this is
// the client mirror per the codebase's "mirror server constants inline"
// pattern, matching how VALID_PARAMETER_TYPES and similar are mirrored).
export const VALID_DOCUMENT_TYPE_SIGNER_ROLE_PAIRS = {
  contract: ['renter'],
  waiver: ['operator', 'participant'],
  agreement: ['operator', 'participant'],
};

export const MERGE_FIELDS = [
  // Customer (9) — auto-populated from booking
  { key: 'customer.firstName', label: 'Customer First Name', category: 'customer' },
  { key: 'customer.lastName', label: 'Customer Last Name', category: 'customer' },
  { key: 'customer.fullName', label: 'Customer Full Name', category: 'customer' },
  { key: 'customer.email', label: 'Customer Email', category: 'customer' },
  // Push 10 Phase E — auto-merged DOB chip. Pairs with `input.dob`
  // (the explicit-at-signing-time chip below in the customer category).
  // Label "Profile DOB" describes the data source rather than role,
  // disambiguating from `input.dob`. Resolver path:
  //   1. customer.profile.protectedData.dateOfBirth (auto-merged from
  //      profile — the verified DOB post-Stripe Identity verification)
  //   2. captures.signerDOB (Step 1 typed value for guest signers)
  //   3. customer-input fallback (renders an empty date input — rare;
  //      would only happen if both profile and Step 1 capture are absent)
  { key: 'customer.dob', label: 'Profile DOB', category: 'customer' },
  { key: 'customer.phone', label: 'Customer Phone', category: 'customer' },
  { key: 'customer.address', label: 'Customer Address', category: 'customer' },
  { key: 'customer.city', label: 'Customer City', category: 'customer' },
  { key: 'customer.state', label: 'Customer State', category: 'customer' },
  { key: 'customer.zip', label: 'Customer Zip', category: 'customer' },

  // Booking (13) — auto-populated from booking. boatMake/boatModel resolve
  // to the listing's underlying boat fields; listingName resolves to the
  // listing's marketing title. Rendering logic lives in Phase B/C.
  // A.4a fix-pass v2 follow-up: booking.addOns added between
  // passengerCount and totalPrice. A.4d resolver: read transaction line
  // items, filter to add-on type (exclude base rental, delivery, service
  // fees), extract names, join with commas. Empty-state UX deferred
  // until A.4d.
  { key: 'booking.bookingNumber', label: 'Booking Number', category: 'booking' },
  { key: 'booking.boatMake', label: 'Boat Make', category: 'booking' },
  { key: 'booking.boatModel', label: 'Boat Model', category: 'booking' },
  { key: 'booking.listingName', label: 'Listing Name', category: 'booking' },
  { key: 'booking.startDate', label: 'Start Date', category: 'booking' },
  { key: 'booking.endDate', label: 'End Date', category: 'booking' },
  { key: 'booking.startTime', label: 'Start Time', category: 'booking' },
  { key: 'booking.endTime', label: 'End Time', category: 'booking' },
  { key: 'booking.duration', label: 'Duration', category: 'booking' },
  { key: 'booking.passengerCount', label: 'Passenger Count', category: 'booking' },
  { key: 'booking.addOns', label: 'Add-ons', category: 'booking' },
  { key: 'booking.totalPrice', label: 'Total Price', category: 'booking' },
  { key: 'booking.cardOnFile', label: 'Card on File', category: 'booking' },
  // Push 8 Phase A.4f follow-up — `booking.last4` is intentionally NOT
  // listed in MERGE_FIELDS so it doesn't appear in the editor palette
  // (we have no real provider templates to preserve, per Cooper's
  // May 14, 2026 polish call). The resolver alias still resolves it
  // to the same composite "BRAND •••• 1234 (exp MM/YY)" string, so
  // any dev test templates referencing the old key continue to
  // render correctly — see the booking.last4 case in
  // src/util/signingTimeResolver.js's resolveBookingField.

  // Customer Input (7) — Phase A.4a unified into the Customer category
  // above. Field keys preserved (`input.*` prefix) so chip content_html
  // and the signing-time resolver continue to differentiate profile-
  // sourced from always-customer-input fields. Only the palette grouping
  // changed.
  //
  // A.4a fix-pass v2 follow-up addendum: the duplicate `input.address`
  // chip was removed — `customer.address` (the canonical chip) handles
  // both the profile-merged and customer-input fallback cases per the
  // unified runtime behavior. Existing templates referencing
  // `input.address` will render `[missing field]` via the A.3.3
  // defensive guard.
  { key: 'input.dob', label: 'Date of Birth', category: 'customer' },
  { key: 'input.driversLicense', label: "Driver's License #", category: 'customer' },
  { key: 'input.driversLicenseState', label: 'DL Issuing State', category: 'customer' },
  { key: 'input.emergencyContactName', label: 'Emergency Contact Name', category: 'customer' },
  { key: 'input.emergencyContactPhone', label: 'Emergency Contact Phone', category: 'customer' },
  // A.4d Item Z polish: helpText surfaces below the input at signing
  // time so the customer has a clear path to satisfy the field even when
  // not applicable. Only built-in chip with helpText today; the field
  // optionally renders via ChipResolver's customer-input fallback.
  { key: 'input.minorsList', label: 'Minors List', category: 'customer', helpText: 'If none, write N/A' },
  { key: 'input.notes', label: 'Notes', category: 'customer' },

  // Custom Parameters — populated by provider in A.3 (empty for now;
  // palette section renders the "no custom parameters yet" empty state)

  // Execution (4) — signature + initials + computed dates. All four
  // anchor the document's execution at signing time. Field keys
  // preserve their original prefixes (`signature.*` and `date.*`); the
  // category key 'signatures' is unchanged in the data model — only
  // the user-facing CATEGORY_LABELS render uses "Execution".
  // A.4a fix-pass v2 follow-up addendum: date.today + date.signedAt
  // moved here from the retired 'dates' category.
  { key: 'signature.lessee', label: 'Signature', category: 'signatures' },
  { key: 'signature.lesseeInitials', label: 'Initials', category: 'signatures' },
  { key: 'date.today', label: "Today's Date", category: 'signatures' },
  { key: 'date.signedAt', label: 'Signed At', category: 'signatures' },
];

/**
 * i18n category labels — the canonical English defaults. The palette renders
 * these via FormattedMessage (`MergeFieldPalette.category.<key>`) so they can
 * be localized later.
 */
export const CATEGORY_LABELS = {
  customer: 'Customer',
  booking: 'Reservation',
  customFields: 'Custom Fields',
  // A.4a fix-pass v2 follow-up: user-facing label renamed to "Execution"
  // (covers the document's signature + initial chips + the two computed
  // date chips that anchor execution — Today's Date and Signed At).
  // Underlying category KEY stays 'signatures' so chip data never moves.
  // The standalone 'dates' category was retired in the same pass; both
  // date chips now live in this category.
  signatures: 'Execution',
};

/** Lookup helper used by MergeFieldChip to render the user-facing label. */
export const findMergeFieldByKey = key => MERGE_FIELDS.find(f => f.key === key) || null;
